import { Box, IconButton, Modal, ModalContent, ModalOverlay, Tooltip } from '@chakra-ui/react';
import { ReactComponent as PreviewIcon } from 'assets/icons/enlarge.svg';
import clonedeep from 'lodash.clonedeep';
import { store } from 'modules/Editor/store';
import { dangerouslySetHasKeyboardIgnore } from 'modules/Editor/store/editorSettingsSlice';
import { updateWidgetNoHistory } from 'modules/Editor/store/infographSlice';
import { resetAllWidgets } from 'modules/ResponsiveLinkView/scaleWidgets';
import { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { WidgetsMap } from 'types/infographTypes';

import { getPreviewScale } from './ResponsiveLinkPreview.helpers';

const ResponsivePreview = lazy(
  () =>
    import(
      /* webpackChunkName: "ResponsivePreview" */
      /* webpackPrefetch: true */
      './ResponsivePreview'
    ),
);

export const ResponsiveLinkPreviewModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // store a copy of the widgets to reset them when the modal is closed
  const originalWidgetsCopy = useRef<WidgetsMap>({} as WidgetsMap);
  const displayRef = useRef<HTMLDivElement>(null);

  // when the modal is closing it should not update the widgets anymore
  const hasCustomResize = useRef(false);
  const customShouldResize = useCallback(() => hasCustomResize.current, []);

  const onOpenModal = useCallback(() => {
    setIsOpen(true);
    hasCustomResize.current = true;
    originalWidgetsCopy.current = clonedeep(store.getState().infograph.widgets);
  }, []);

  const onCloseModal = useCallback(() => {
    hasCustomResize.current = false;
    if (displayRef.current) displayRef.current.style.display = 'none';

    // reset the widgets to their original state (preview can change widget size/position)
    const updateWidgets = resetAllWidgets(originalWidgetsCopy.current);
    store.dispatch(updateWidgetNoHistory(updateWidgets));
    originalWidgetsCopy.current = {} as WidgetsMap;

    store.dispatch(dangerouslySetHasKeyboardIgnore({ hasKeyboardIgnore: false }));
    setIsOpen(false);
  }, []);

  const toggleModal = useCallback(() => {
    if (isOpen) return onCloseModal();
    onOpenModal();
  }, [isOpen, onOpenModal, onCloseModal]);

  const { scaledHeight } = getPreviewScale();
  const label = 'Preview';
  const BOTTOM_MARGIN_PX = 130;

  return (
    <>
      <Tooltip hasArrow placement='bottom' label={label}>
        <IconButton
          aria-label={label}
          variant='icon-btn-toolbar'
          size='sm'
          icon={<PreviewIcon />}
          onClick={toggleModal}
          border='1px solid'
          borderColor='gray.200'
        />
      </Tooltip>
      <Modal isOpen={isOpen} onClose={onCloseModal} isCentered>
        <ModalOverlay bg='rgba(0,0,0,95)' />
        <ModalContent overflow='hidden' w='6xl' maxW='6xl' maxH='6xl' bg='privateLinkView.wrapperBg'>
          <Box height={scaledHeight + BOTTOM_MARGIN_PX + 'px'}>
            <Suspense fallback={<div>loading...</div>}>
              <ResponsivePreview
                displayRef={displayRef}
                customShouldResize={customShouldResize}
                onClose={onCloseModal}
              />
            </Suspense>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};
